import React, { useState, useEffect } from "react";
import {
  Box,
  Tabs,
  Tab,
  Typography,
  Paper,
  useMediaQuery,
  useTheme,
  Stack,
  Divider,
} from "@mui/material";
import {
  People as PeopleIcon,
  AddCard as AddCardIcon,
  History as HistoryIcon,
  Calculate as CalculateIcon,
} from "@mui/icons-material";
import EmployeeManager from "../components/payroll/EmployeeManager";
import RecordPayroll from "../components/payroll/RecordPayroll";
import PayrollHistory from "../components/payroll/PayrollHistory";
import DirectorSalaryOptimizer from "../components/payroll/DirectorSalaryOptimizer";
import { db, auth } from "../firebase";
import { collection, query, where, getDocs } from "firebase/firestore";

export default function Payroll() {
  const [tab, setTab] = useState(0);
  const [monthlyTotal, setMonthlyTotal] = useState(0);
  const [yearlyTotal, setYearlyTotal] = useState(0);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Fetch payroll totals
  useEffect(() => {
    const fetchTotals = async () => {
      const user = auth.currentUser;
      if (!user) return;
      const q = query(collection(db, "payroll"), where("uid", "==", user.uid));
      const snap = await getDocs(q);
      const now = new Date();
      const thisMonth = now.getMonth();
      const thisYear = now.getFullYear();
      let monthSum = 0;
      let yearSum = 0;
      snap.forEach((doc) => {
        const { amount, createdAt } = doc.data();
        const d = createdAt?.toDate?.() || new Date(createdAt);
        if (d.getFullYear() === thisYear) {
          yearSum += parseFloat(amount || 0);
          if (d.getMonth() === thisMonth) {
            monthSum += parseFloat(amount || 0);
          }
        }
      });
      setMonthlyTotal(monthSum);
      setYearlyTotal(yearSum);
    };
    fetchTotals();
  }, []);

  const handleTabChange = (event, newValue) => setTab(newValue);

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 2, fontWeight: 700 }}>
        Payroll
      </Typography>

      {/* Payroll totals summary */}
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={2}
        sx={{ mb: 3 }}
        alignItems="stretch"
      >
        <Paper
          elevation={2}
          sx={{
            flex: 1,
            p: 2,
            background: "linear-gradient(90deg, #c7d2fe 0%, #fbcfe8 100%)",
            color: "#1e293b",
            borderRadius: 3,
            minWidth: 220,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
          }}
        >
          <Typography fontWeight={600} variant="body2" mb={0.5}>
            <span role="img" aria-label="month">
              📅
            </span>{" "}
            Total Paid This Month
          </Typography>
          <Typography variant="h4" fontWeight={700}>
            €
            {monthlyTotal.toLocaleString(undefined, {
              minimumFractionDigits: 2,
            })}
          </Typography>
        </Paper>
        <Paper
          elevation={2}
          sx={{
            flex: 1,
            p: 2,
            background: "linear-gradient(90deg, #a7f3d0 0%, #fde68a 100%)",
            color: "#1e293b",
            borderRadius: 3,
            minWidth: 220,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
          }}
        >
          <Typography fontWeight={600} variant="body2" mb={0.5}>
            <span role="img" aria-label="year">
              🗓️
            </span>{" "}
            Total Paid This Year
          </Typography>
          <Typography variant="h4" fontWeight={700}>
            €
            {yearlyTotal.toLocaleString(undefined, {
              minimumFractionDigits: 2,
            })}
          </Typography>
        </Paper>
      </Stack>

      <Paper elevation={3} sx={{ mb: 3 }}>
        <Tabs
          value={tab}
          onChange={handleTabChange}
          variant={isMobile ? "scrollable" : "standard"}
          scrollButtons="auto"
          indicatorColor="primary"
          textColor="primary"
        >
          <Tab
            icon={<CalculateIcon />}
            iconPosition="start"
            label="Director Salary"
            sx={{ minHeight: 48 }}
          />
          <Tab
            icon={<PeopleIcon />}
            iconPosition="start"
            label="Employees"
            sx={{ minHeight: 48 }}
          />
          <Tab
            icon={<AddCardIcon />}
            iconPosition="start"
            label="Submit Payroll"
            sx={{ minHeight: 48 }}
          />
          <Tab
            icon={<HistoryIcon />}
            iconPosition="start"
            label="History"
            sx={{ minHeight: 48 }}
          />
        </Tabs>
      </Paper>

      <Box>
        {tab === 0 && <DirectorSalaryOptimizer />}
        {tab === 1 && <EmployeeManager />}
        {tab === 2 && <RecordPayroll />}
        {tab === 3 && <PayrollHistory />}
      </Box>
    </Box>
  );
}
